import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import got from "got";
import {
  getAnnouncementBanner,
  getPage,
  getDefaultLogos,
  type Banner,
  type Page as SanityPage,
  type Customer,
  getDefaultMobileLogos,
} from "lib/sanity";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { StackedLogos } from "components/logos";
import { DirectionalButton } from "ui/button";
import { TextTitle } from "ui/text";
import { getPageMetaImage } from "utils";
import { Benefits, CareersHero, Values, type Deparment, JobListings, ForbesAward } from "components/careers";

type PageProps = {
  departments: Deparment[];
  page: SanityPage;
  announcementBanner?: Banner;
  defaultLogos: Customer[];
  defaultMobileLogos: Customer[];
};

const Careers: NextPage<Readonly<PageProps>> = ({
  departments,
  page,
  announcementBanner,
  defaultLogos,
  defaultMobileLogos,
}) => {
  const openPositionsId = "open-positions";

  return (
    <Page
      hasSmoothScroll
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.blue.150"
      footerOptions={{ variant: "blue" }}
    >
      <CareersHero title={page.title} openPositionsId={openPositionsId} />

      <Section my="0" pt={{ base: "big", md: "biggest" }}>
        <TextTitle
          size="md"
          title="Let’s grow together"
          description="At Hightouch, we’re committed to helping our customers, business, and employees grow. As a series C startup backed by top investors, we are determined to continuously raise the bar and provide the best product in the market. Grow your career in a fast paced environment that values creative thinking and innovation."
          actions={
            <DirectionalButton variant="secondary" href="/about" pl="0">
              More about Hightouch
            </DirectionalButton>
          }
          maxW="3xl"
          mx="auto"
        />
      </Section>

      <StackedLogos
        customers={defaultLogos}
        mobileCustomers={defaultMobileLogos}
        title="We're proud to serve the most amazing companies out there"
        titleMaxW="4xl"
        my={0}
        py={{ base: "biggest", md: "huger" }}
        pb={sectionSpacingY}
      />

      <Values />

      <Benefits />

      <JobListings titleId={openPositionsId} departments={departments} />

      <ForbesAward />
    </Page>
  );
};

export default Careers;

type RawDepartment = {
  id: number;
  name: string;
  jobs: Array<{
    id: number;
    title: string;
    location: {
      name: string;
    };
    absolute_url: string;
  }>;
};

const fetchDepartments = async () => {
  const response = await got<{ departments: RawDepartment[] }>(
    `https://boards-api.greenhouse.io/v1/boards/hightouch/departments`,
    {
      responseType: "json",
    },
  );

  return response.body.departments;
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const rawDepartments = await fetchDepartments();
  const [page, announcementBanner, defaultLogos, defaultMobileLogos] = await Promise.all([
    getPage("/careers"),
    getAnnouncementBanner(),
    getDefaultLogos(),
    getDefaultMobileLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const departments = rawDepartments
    .filter((department) => department.id !== 0)
    .filter((department) => department.jobs.length > 0)
    .map((department) => ({
      id: department.id,
      name: department.name,
      jobs: department.jobs.map((job) => ({
        id: job.id,
        url: job.absolute_url,
        location: job.location.name,
        title: job.title,
      })),
    }));

  return {
    props: {
      page,
      announcementBanner,
      defaultLogos,
      defaultMobileLogos,
      departments,
    },
    revalidate: 60,
  };
};
